import React, { useRef, useState, useContext, useEffect } from 'react';
import { BiVideo } from 'react-icons/bi';
import Cropper from "../../../../src/cropper/components/CroppersWizard.tsx";
import { Language, LoginUserData } from '../../../App';
import { _imagesURL, adminBaseURL } from '../../../constants/API';
import { getToken } from '../../../shared/token.js';
import PostImageModal from './PostImageModal';
import { RiImageAddLine, RiSparkling2Fill, RiUploadCloud2Line } from 'react-icons/ri';
import { IoCloseSharp } from 'react-icons/io5';
import { toast } from 'react-toastify';
import axios from 'axios';
import ImagePreviewModal from '../../common/ImagePreviewModal';
import { useCheckSubscriptionPermission } from '../../../hooks/useCheckSubscriptionPermission';
import MixpanelManager from '../../../constants/MixpanelManager';
import { MixpanelEvents } from '../../../constants/MixpanelEvents';
import { releaseAgendaScrollLock } from '../../../utils/agendaUtils';
import './formTheme.css';

const PostTab = ({
  onHide,
  setProgress,
  handleApiCall,
  editPost,
  setEditPost,
  selectedPost,
  handlePostSelect,
  updatePost,
  refetch
}) => {
  const { lang } = useContext(Language);
  const { splashData } = useContext(LoginUserData);
  const checkPermission = useCheckSubscriptionPermission('upload_post');
  const { data: userToken } = JSON.parse(localStorage.getItem("SignupData") || "{}");
  const childRef = useRef(null);
  const [caption, setCaption] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const maxNumber = 6;
  const [images, setImages] = useState([]);
  const [imagesArr, setImagesArr] = useState([]);
  const [fileUploadRefs, setFileUploadRefs] = useState({});
  const [numberOfImages, setNumberOfImages] = useState(
    Object.keys(images).length < maxNumber
      ? Object.keys(images).length
      : maxNumber
  );
  const [ar, setAR] = useState(undefined);
  const [minARatio, setMinAR] = useState(undefined);
  const [maxARatio, setMaxAR] = useState(undefined);
  const [height, setHeight] = useState(undefined);
  const [width, setWidth] = useState(undefined);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [resizable, setResizable] = useState(true);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [updateCoordinatesCheck, setUpdateCoordinatesCheck] = useState(true);
  const [hasSettings, setHasSettings] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Prefill fields when editing an existing post
  useEffect(() => {
    if (editPost && selectedPost) {
      setCaption(selectedPost.caption || selectedPost.description || '');

      if (selectedPost.media && Array.isArray(selectedPost.media) && selectedPost.media.length > 0) {
        if (selectedPost.type === "video") {
          const vUrl = selectedPost.media[0].startsWith("http")
            ? selectedPost.media[0]
            : _imagesURL + selectedPost.media[0];
          setVideoUrl(vUrl);
          setVideoSrc(selectedPost.media[0]);
        } else {
          const existingImgs = selectedPost.media.map((img) =>
            typeof img === "string" && (img.startsWith("http") || img.startsWith("data:image/"))
              ? img
              : _imagesURL + img
          );
          setImagesArr(existingImgs);
          setImages(existingImgs);
        }
      } else if (selectedPost.image) {
        const imgUrl = selectedPost.image.startsWith("http") || selectedPost.image.startsWith("data:image/")
          ? selectedPost.image
          : _imagesURL + selectedPost.image;
        setImagesArr([imgUrl]);
        setImages([imgUrl]);
      }
    }
  }, [editPost, selectedPost]);

  // States for Image Preview in Modal
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const toggleImagePreview = () => setIsImagePreviewOpen(!isImagePreviewOpen);
  const handleImageClick = (img) => {
    setPreviewImage(img);
    toggleImagePreview();
  };

  const handleClose = () => {
    setCropperOpen(false);
  };

  const onImageRemove = (index) => {
    const updatedImages = [...imagesArr].filter((_, i) => i !== index);

    setImages(updatedImages);
    setImagesArr(updatedImages);

    const newCount = updatedImages.length < maxNumber ? updatedImages.length + 1 : updatedImages.length;
    setNumberOfImages(newCount);

    // Clear value on existing file inputs so the same image file can be picked again
    Object.values(fileUploadRefs || {}).forEach((r) => {
      if (r?.current) {
        r.current.value = '';
      }
    });

    if (updatedImages.length === 0) {
      setHasSettings(true);
      setAR(undefined);
      setMinAR(undefined);
      setMaxAR(undefined);
      setWidth(undefined);
      setHeight(undefined);
      setTop(0);
      setLeft(0);
      setCropperOpen(false);
    }
  };

  const handleImageUploadClick = () => {
    if (imagesArr.length >= maxNumber) {
      toast.warning(`You can only upload up to ${maxNumber} images.`);
      return;
    }
    const currentIndex = numberOfImages - 1;
    let inputEl = fileUploadRefs?.[currentIndex]?.current;
    if (!inputEl) {
      inputEl = Object.values(fileUploadRefs || {}).find((r) => r?.current)?.current;
    }
    if (inputEl) {
      inputEl.value = '';
      inputEl.click();
    }
  };

  const getVideoSource = (src) => {
    const url = URL.createObjectURL(src);
    setVideoSrc(src);
    setVideoUrl(url);
  };

  const handleUpdate = async () => {
    if (!videoSrc && !videoUrl && imagesArr.length === 0 && caption.trim().length === 0) {
      toast.error(lang?.media_is_required || "Please provide text content or upload media.");
      return;
    }

    try {
      const pid = selectedPost?.original_id || selectedPost?.id;
      const newImagesBase64 = imagesArr.filter((img) => typeof img === "string" && img.startsWith("data:image/"));
      const existingImages = imagesArr
        .filter((img) => typeof img === "string" && !img.startsWith("data:image/"))
        .map((img) => (img.startsWith(_imagesURL) ? img.replace(_imagesURL, "") : img));

      const editData = {
        post_id: pid,
        caption: caption,
        type: imagesArr.length > 0 ? "image" : (videoSrc || videoUrl) ? "video" : "text",
      };

      if (newImagesBase64.length > 0) {
        editData.mediaBase64 = newImagesBase64;
      }
      if (existingImages.length > 0) {
        editData.existing_media = existingImages;
      }
      if (videoSrc && typeof videoSrc !== "string") {
        editData.media = [videoSrc];
      }

      const response = await axios.post(
        adminBaseURL + "user/post/update",
        editData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${userToken?.token || getToken()}`,
          },
        }
      );

      if (response?.data?.status) {
        MixpanelManager.track(MixpanelEvents.editPost, {
          post_id: pid,
          caption_length: caption.length,
          post_type: imagesArr.length > 0 ? "image" : (videoSrc || videoUrl) ? "video" : "text",
        });
        toast.success(lang?.post_updated_successfully || "Post updated successfully");
        if (setEditPost) setEditPost(false);
        releaseAgendaScrollLock(true);
        onHide();
        if (refetch) refetch();
        if (handlePostSelect) {
          handlePostSelect(response.data.data || { ...selectedPost, caption });
        }
        if (updatePost) {
          updatePost(pid, caption);
        }
        return response;
      } else {
        toast.error(response?.data?.action || response?.data?.error || "Failed to update post");
      }
    } catch (error) {
      console.error("Error updating post:", error);
      toast.error(lang?.failed_upload_please_try_again_later || "Update failed. Please try again.");
    }
  };

  const handleUpload = async () => {
    if (!videoSrc && imagesArr.length === 0 && caption.trim().length === 0) {
      toast.error(lang?.media_is_required || "Please provide text content or upload media.");
      return;
    }

    try {
      const data = {
        mediaBase64: imagesArr,
        media: [videoSrc],
        type: imagesArr.length > 0 ? "image" : videoSrc ? "video" : "text",
        caption,
      };
      const response = await axios.post(
        adminBaseURL + "user/post/create",
        data,
        {
          onUploadProgress: (progressEvent) => {
            setProgress(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          },
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${userToken?.token}`,
          },
        }
      );
      if (response.data.status) {
        MixpanelManager.track(MixpanelEvents.createPost, {
          post_type: imagesArr.length > 0 ? 'image' : videoSrc ? 'video' : 'text',
          media_count: imagesArr.length > 0 ? imagesArr.length : videoSrc ? 1 : 0,
          has_caption: caption.length > 0,
        });
        return response;
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      console.error("Error uploading:", error);
      toast.error(lang?.failed_upload_please_try_again_later || "Upload failed. Please try again.");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedVideo(file);
      const url = URL.createObjectURL(file);
      setVideoSrc(file);
      setVideoUrl(url);
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer?.files || []);
    if (!files || files.length === 0) return;

    const firstFile = files[0];
    if (firstFile.type.startsWith("video/")) {
      setSelectedVideo(firstFile);
      const url = URL.createObjectURL(firstFile);
      setVideoSrc(firstFile);
      setVideoUrl(url);
    } else if (firstFile.type.startsWith("image/")) {
      handleImageUploadClick();
    }
  };

  const handleVideoUploadClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "video/*";
    input.addEventListener("change", handleFileChange);
    input.click();
  };

  const _handleSubmit = async () => {
    if (editPost) {
      await handleUpdate();
      return;
    }
    if (!checkPermission()) return;
    releaseAgendaScrollLock(true);
    onHide();
    await handleApiCall(() => handleUpload(), { type: "post" });
  };

  return (
    <>
      <div className="pf-form-body">
        <div className="pf-form-content">
          {/* 1. Header Banner */}
          <div className="pf-ai-banner">
            <div className="pf-ai-banner-left">
              <RiSparkling2Fill className="pf-ai-banner-sparkle" size={15} />
              <span>{editPost ? (lang?.edit_post || "Edit Post") : "Clinical Knowledge & Case Sharing"}</span>
            </div>
            <span className="pf-ai-banner-tag">{editPost ? (lang?.editing || "Editing") : "Verified Peer Feed"}</span>
          </div>

          {/* 2. Media Uploader Placeholder (Above Description) */}
          {imagesArr.length === 0 && !videoUrl && (
            <div
              className={`pf-media-dropzone ${isDragging ? "pf-media-dropzone-active" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="pf-media-dropzone-inner">
                <div className="pf-media-dropzone-icon-wrap">
                  <div className="pf-media-dropzone-icon">
                    <RiImageAddLine size={24} />
                  </div>
                  <div className="pf-media-dropzone-badge-icon">
                    <BiVideo size={13} />
                  </div>
                </div>

                <div className="pf-media-dropzone-text">
                  <div className="pf-media-dropzone-title">
                    {lang?.share_images_or_videos_in_your_post || "Add Clinical Images or Video"}
                  </div>
                  <div className="pf-media-dropzone-desc">
                    Upload diagnostic cases, OCT scans, or procedural clips here
                  </div>
                  <div className="pf-media-dropzone-hint">
                    Supports high-res JPG, PNG, WEBP (up to 6 photos) or MP4 video
                  </div>
                </div>

                <div className="pf-media-dropzone-actions">
                  <button
                    type="button"
                    className="pf-dropzone-action-btn pf-dropzone-btn-primary"
                    onClick={handleImageUploadClick}
                  >
                    <RiImageAddLine size={15} />
                    <span>{lang?.images || "Browse Images"}</span>
                  </button>
                  <button
                    type="button"
                    className="pf-dropzone-action-btn pf-dropzone-btn-secondary"
                    onClick={handleVideoUploadClick}
                  >
                    <BiVideo size={15} />
                    <span>{lang?.video || "Upload Video"}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 3. Media Preview Section (When media is attached) */}
          {(imagesArr.length > 0 || videoUrl) && (
            <div className="pf-card-section">
              {imagesArr.length > 0 && (
                <div className="pf-thumb-grid">
                  {imagesArr.map((img, index) => (
                    <div
                      key={index}
                      className="pf-thumb"
                    >
                      <img
                        className="cursor"
                        onClick={() => handleImageClick(img)}
                        src={img}
                        alt="Uploaded"
                      />
                      <button
                        type="button"
                        className="pf-thumb-remove"
                        aria-label="Remove"
                        onClick={() => onImageRemove(index)}
                      >
                        <IoCloseSharp size={14} />
                      </button>
                    </div>
                  ))}

                  {/* Dotted Tile Placeholder in the same grid */}
                  {imagesArr.length < maxNumber && (
                    <div
                      className="pf-thumb-add-tile"
                      onClick={handleImageUploadClick}
                      role="button"
                      tabIndex={0}
                      aria-label="Add more images"
                    >
                      <div className="pf-thumb-add-tile-icon">
                        <RiUploadCloud2Line size={21} />
                      </div>
                      <span className="pf-thumb-add-tile-count">
                        {imagesArr.length}/{maxNumber}
                      </span>
                    </div>
                  )}
                </div>
              )}

                {videoUrl && (
                  <div className="pf-video-preview-wrap">
                    <video
                      src={videoUrl}
                      className="pf-video-player"
                      controls
                      playsInline
                    />
                    <button
                      type="button"
                      className="pf-video-remove-btn"
                      aria-label="Remove video"
                      onClick={() => {
                        setVideoSrc(null);
                        setVideoUrl(null);
                        setSelectedVideo(null);
                      }}
                    >
                      <IoCloseSharp size={16} />
                    </button>
                  </div>
                )}
            </div>
          )}

          {!videoUrl && (
            <Cropper
              images={images}
              setImages={setImages}
              imagesArr={imagesArr}
              setImagesArr={setImagesArr}
              max={maxNumber}
              allowCrop={true}
              ar={ar}
              minARatio={minARatio}
              maxARatio={maxARatio}
              setAR={setAR}
              setMaxAR={setMaxAR}
              setMinAR={setMinAR}
              resizable={resizable}
              setResizable={setResizable}
              height={height}
              setHeight={setHeight}
              width={width}
              setWidth={setWidth}
              top={top}
              setTop={setTop}
              left={left}
              setLeft={setLeft}
              hasSettings={hasSettings}
              setHasSettings={setHasSettings}
              updateCoordinatesCheck={updateCoordinatesCheck}
              setUpdateCoordinatesCheck={setUpdateCoordinatesCheck}
              handleClose={handleClose}
              fileUploadRefs={fileUploadRefs}
              setFileUploadRefs={setFileUploadRefs}
              numberOfImages={numberOfImages}
              setNumberOfImages={setNumberOfImages}
              cropperOpen={cropperOpen}
              setCropperOpen={setCropperOpen}
            />
          )}

          {/* 4. Post Content / Description Field (Below Media) */}
          <div className="pf-field">
            <div className="pf-label">
              <span className="pf-label-text">Clinical Insights & Notes</span>
              <span className="pf-optional-badge">Markdown supported</span>
            </div>
            <textarea
              className="pf-textarea"
              placeholder={`${lang?.whats_on_your_mind || "What clinical insights would you like to share"}, ${splashData?.user?.name?.split(' ')[0] || "Doctor"}?`}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={4}
              style={{ fontSize: "14px", lineHeight: "1.6" }}
            />
          </div>
        </div>

        {/* 6. Sticky Footer Action Bar (Statically anchored at the bottom) */}
        <div className="pf-footer-row">
          <button
            type="button"
            className="pf-btn-submit w-100"
            disabled={!(imagesArr.length > 0 || videoSrc || videoUrl || caption.trim().length > 0)}
            onClick={_handleSubmit}
          >
            <span>{editPost ? (lang?.update || "Update Post") : (lang?.publish || lang?.upload || "Publish Post")}</span>
          </button>
        </div>
      </div>

      <PostImageModal
        ref={childRef}
        getVideoSource={getVideoSource}
        showModal={showImageModal}
        setShowModal={setShowImageModal}
      />
      <ImagePreviewModal
        isOpen={isImagePreviewOpen}
        toggle={toggleImagePreview}
        imageSrc={previewImage}
        alt="Post Preview"
        zIndex={10060}
      />
    </>
  );
};

export default PostTab;
